import axios from 'axios';
import { BASE_PATH } from 'api/base';

const api = axios.create({ baseURL: BASE_PATH });

// Attach auth token from localStorage
api.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem('user') as string);
  if (user?.accessToken) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${user.accessToken}`,
    };
  }
  return config;
});

// ─── Enums ──────────────────────────────────────────

export enum CollectionChannel {
  Email = 1,
  Sms = 2,
  WhatsApp = 3,
  Telegram = 4,
  Push = 5,
  InternalNotification = 6,
  Webhook = 7,
}

export enum CollectionActionType {
  SendMessage = 1,
  EscalateToManager = 2,
  FlagDeal = 3,
  Webhook = 4,
}

export enum CollectionLogStatus {
  Pending = 1,
  Sent = 2,
  Delivered = 3,
  Failed = 4,
  Skipped = 5,
}

// ─── DTOs ───────────────────────────────────────────

export interface DebtCollectionConfigDto {
  id?: number;
  organizationId?: number;
  isEnabled?: boolean;
  timeZoneId?: string | null;
  rules?: DebtCollectionRuleDto[];
  channelConfigs?: DebtCollectionChannelConfigDto[];
  templates?: DebtCollectionTemplateDto[];
}

export interface DebtCollectionRuleDto {
  id?: number;
  configId?: number;
  stepOrder?: number;
  daysOverdue?: number;
  actionType?: CollectionActionType;
  channel?: CollectionChannel;
  templateId?: number | null;
  templateName?: string | null;
  webhookUrl?: string | null;
  escalateToUserId?: string | null;
  flagDealAsCollections?: boolean;
  isActive?: boolean;
}

export interface DebtCollectionChannelConfigDto {
  id?: number;
  configId?: number;
  channel?: CollectionChannel;
  isEnabled?: boolean;
  credentialVersion?: number;
  hasCredentials?: boolean;
}

export interface DebtCollectionTemplateDto {
  id?: number;
  configId?: number;
  channel?: CollectionChannel;
  name?: string | null;
  subject?: string | null;
  body?: string | null;
  isDefault?: boolean;
}

export interface DebtCollectionLogDto {
  id?: number;
  cashFlowId?: number;
  ruleId?: number;
  organizationId?: number;
  channel?: CollectionChannel;
  actionType?: CollectionActionType;
  status?: CollectionLogStatus;
  recipientIdentifier?: string | null;
  renderedMessage?: string | null;
  errorDetails?: string | null;
  externalMessageId?: string | null;
  executedAt?: string;
  stepOrder?: number;
  clientName?: string | null;
  dealId?: number | null;
}

export interface DebtCollectionDashboardDto {
  totalOverdue?: number;
  activeEscalations?: number;
  messagesSentToday?: number;
  successRate?: number;
  stepBreakdown?: StepBreakdownDto[];
  channelStats?: ChannelStatsDto[];
}

export interface StepBreakdownDto {
  stepOrder?: number;
  count?: number;
}

export interface ChannelStatsDto {
  channel?: CollectionChannel;
  totalSent?: number;
  totalFailed?: number;
  totalSkipped?: number;
}

export interface GetDebtCollectionLogsResult {
  items?: DebtCollectionLogDto[];
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
}

export interface PreviewTemplateResult {
  renderedSubject?: string | null;
  renderedBody?: string | null;
}

// ─── API Service ────────────────────────────────────

const BASE = '/api/debt-collection';

export const debtCollectionApi = {
  // Config
  getConfig: () => api.get<DebtCollectionConfigDto>(`${BASE}/config`),
  createConfig: (data: Partial<DebtCollectionConfigDto>) =>
    api.post<number>(`${BASE}/config`, data),
  updateConfig: (data: Partial<DebtCollectionConfigDto>) =>
    api.put(`${BASE}/config`, data),

  // Rules
  getRules: () => api.get<DebtCollectionRuleDto[]>(`${BASE}/rules`),
  createRule: (data: Partial<DebtCollectionRuleDto>) =>
    api.post<number>(`${BASE}/rules`, data),
  updateRule: (id: number, data: Partial<DebtCollectionRuleDto>) =>
    api.put(`${BASE}/rules/${id}`, data),
  deleteRule: (id: number) => api.delete(`${BASE}/rules/${id}`),
  reorderRules: (ruleIds: number[]) =>
    api.put(`${BASE}/rules/reorder`, { ruleIds }),

  // Channels
  getChannels: () =>
    api.get<DebtCollectionChannelConfigDto[]>(`${BASE}/channels`),
  upsertChannel: (channel: CollectionChannel, data: { isEnabled: boolean; credentialsJson?: string }) =>
    api.put<number>(`${BASE}/channels/${channel}`, { ...data, channel }),
  testChannel: (channel: CollectionChannel) =>
    api.post<{ success: boolean; message: string }>(`${BASE}/channels/${channel}/test`),

  // Templates
  getTemplates: () =>
    api.get<DebtCollectionTemplateDto[]>(`${BASE}/templates`),
  createTemplate: (data: Partial<DebtCollectionTemplateDto>) =>
    api.post<number>(`${BASE}/templates`, data),
  updateTemplate: (id: number, data: Partial<DebtCollectionTemplateDto>) =>
    api.put(`${BASE}/templates/${id}`, data),
  deleteTemplate: (id: number) => api.delete(`${BASE}/templates/${id}`),
  previewTemplate: (data: { templateBody: string; templateSubject?: string }) =>
    api.post<PreviewTemplateResult>(`${BASE}/templates/preview`, data),

  // Logs
  getLogs: (params: {
    pageNumber?: number;
    pageSize?: number;
    channel?: CollectionChannel;
    status?: CollectionLogStatus;
    fromDate?: string;
    toDate?: string;
  }) => api.get<GetDebtCollectionLogsResult>(`${BASE}/logs`, { params }),

  // Dashboard
  getDashboard: () =>
    api.get<DebtCollectionDashboardDto>(`${BASE}/dashboard`),

  // Actions
  resolve: (cashFlowId: number) =>
    api.post(`${BASE}/resolve/${cashFlowId}`),
};
